import type { ReactNode } from 'react';
import type { SessionSummary } from '@features/customization-session/types';

import { CircleCheck, TriangleAlert, Info as InfoIcon } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { Card } from '@shared/components/ui/card';
import { Stepper } from '@shared/components/ui/stepper';
import { Progress } from '@shared/components/ui/progress';
import { Alert, AlertTitle } from '@shared/components/ui/alert';

import { STATUS_STEPS } from '@features/customization-session/utils/session-status';

type Props = {
  summary: SessionSummary;
  /** What the operator has to do at this step. */
  instruction: ReactNode;
  severity: 'info' | 'warning' | 'success';
  /** The actions of the step, the main one first. */
  children?: ReactNode;
};

/** Number of products done in the current step, when the step is validated product by product. */
const getProgress = ({ status, loaded, stored }: SessionSummary): { done: number; label: string } | null => {
  if (status === 'to_load') return { done: loaded, label: 'dans le gabarit' };
  if (status === 'customized') return { done: stored, label: 'rangés' };
  return null;
};

const ALERT_ICONS = { info: InfoIcon, warning: TriangleAlert, success: CircleCheck } as const;

/**
 * Side panel of a session: where the operator is in the process, what to do now, and the actions. It stays in view while
 * the list of products scrolls, so the main action is never out of reach.
 */
export const SessionPanel = ({ summary, instruction, severity, children }: Props) => {
  const activeStep =
    summary.status === 'stored' ? STATUS_STEPS.length : STATUS_STEPS.findIndex((s) => s.value === summary.status);
  const progress = getProgress(summary);
  const AlertIcon = ALERT_ICONS[severity];

  return (
    <Card className="sticky top-6 gap-5 p-5">
      <Stepper
        activeStep={activeStep}
        aria-label="Étapes de la session"
        steps={STATUS_STEPS.map((step) => step.step)}
      />

      <Alert
        className={cn('items-center', severity === 'info' && 'border-info/50 text-info')}
        variant={severity === 'info' ? 'default' : severity}
      >
        <AlertIcon />
        <AlertTitle className="line-clamp-none">{instruction}</AlertTitle>
      </Alert>

      {progress && (
        <div aria-live="polite" className="flex flex-col gap-1.5">
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold tabular-nums md:text-2xl">{progress.done}</span>
            <span className="text-sm text-muted-foreground tabular-nums">
              / {summary.quantity} {progress.label}
            </span>
          </div>
          <Progress
            aria-label="Avancement de l'étape"
            className={cn('h-1.5', progress.done === summary.quantity && 'bg-success/24 [&>div]:bg-success')}
            value={summary.quantity === 0 ? 0 : (progress.done / summary.quantity) * 100}
          />
        </div>
      )}

      <div className="flex flex-col gap-2">{children}</div>
    </Card>
  );
};
